import React, { useEffect, useState, useRef, Fragment, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import config from '../config';
import Cookies from 'js-cookie';
import axios from 'axios';
import Notification from '../components/Notification';
import moment from 'moment';
import 'moment/locale/fr'; // Import French locale
// Only import modern CSS - cleaner design
import '../styles/ChatRoomModern.css';
import { FaDownload } from 'react-icons/fa';

// Modern messaging hooks
import {
    useMessages,
    useSendMessage,
    useMarkAsRead,
    useRealtimeMessages,
    useEditMessage,
    useToggleReaction
} from '../hooks/useMessaging';
import { useQueryClient } from '@tanstack/react-query';
import modernMessagingService from '../services/ModernMessagingService';
import encryptionService from '../services/EncryptionService';
import MessageReactions from '../components/MessageReactions';
import ReportModal from '../components/ReportModal';

// Search functionality
import ConversationSearch from '../components/ConversationSearch';
import { useConversationSearch } from '../hooks/useConversationSearch';
import { useSelector } from 'react-redux';
import moderationService from '../services/moderationService';

// New component imports
import ChatHeader from '../components/chat/ChatHeader';
import MessageList from '../components/chat/MessageList';
import MessageInput from '../components/chat/MessageInput';

// Set moment to use French locale globally for this component
moment.locale('fr');

const ConversationMessages = ({ onLoginClick }) => {
    const { conversationId: paramConversationId, userId, productId, productOwnerId } = useParams();
    const location = useLocation();
    
    // Extract URL parameters passed from navigation (similar to mobile app pattern)
    const urlParams = new URLSearchParams(location.search);
    const paramProductName = urlParams.get('productName');
    const paramProductPrice = urlParams.get('productPrice');
    const paramProductCurrency = urlParams.get('productCurrency');
    const paramProductImage = urlParams.get('productImage');
    const paramCategoryName = urlParams.get('categoryName');
    const paramCategoryId = urlParams.get('categoryId');
    const paramOwnerName = urlParams.get('ownerName');
    const paramOwnerPhone = urlParams.get('ownerPhone');
    const paramOwnerProfilePicture = urlParams.get('ownerProfilePicture');
    const paramLastUpdate = urlParams.get('lastUpdate');
    const paramProductOwnerIdFromQuery = urlParams.get('productOwnerId');
    const paramRecipientIdFromQuery = urlParams.get('recipientId');
    const paramRecipientUuid = urlParams.get('recipientUuid');
    const paramProductOwnerUuid = urlParams.get('productOwnerUuid');

    const sanitizeIdValue = (value) => {
        if (!value || value === 'null' || value === 'undefined') return null;
        return value;
    };

    const normalizedProductOwnerId = sanitizeIdValue(paramProductOwnerIdFromQuery) || sanitizeIdValue(productOwnerId);
    const normalizedRecipientId = sanitizeIdValue(paramRecipientIdFromQuery) || normalizedProductOwnerId;

    const [conversationId, setConversationId] = useState(paramConversationId || null);
    const [recipientId, setRecipientId] = useState(normalizedRecipientId || null);
    const [actualProductOwnerId, setActualProductOwnerId] = useState(normalizedProductOwnerId || null);
    const [notification, setNotification] = useState({ message: '', type: 'success', header: '' });
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportTarget, setReportTarget] = useState({ type: '', id: '', name: '' });
    const [replyContent, setReplyContent] = useState('');
    const [productName, setProductName] = useState(paramProductName || '');
    const [productPrice, setProductPrice] = useState(paramProductPrice || '');
    const [productCurrency, setProductCurrency] = useState(paramProductCurrency || 'CDF');
    const [productDetailsLoaded, setProductDetailsLoaded] = useState(!!paramProductName); // Mark as loaded if we have params
    const [productDetails, setProductDetails] = useState(
        (paramProductName && paramProductPrice) ? {
            name: paramProductName,
            price: paramProductPrice,
            currency: paramProductCurrency || 'CDF',
            category_name: paramCategoryName,
            category_id: paramCategoryId,
            owner_name: paramOwnerName,
            featured_image: paramProductImage,
            images: paramProductImage ? [{ image: paramProductImage }] : []
        } : null
    );
    const [userDetails, setUserDetails] = useState({});
    const [userColors, setUserColors] = useState({});
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showMediaOptions, setShowMediaOptions] = useState(false);
    const [attachments, setAttachments] = useState([]);

    const resolvedOwnerId = useMemo(() => {
        return productOwnerId || actualProductOwnerId || normalizedProductOwnerId || recipientId || normalizedRecipientId || null;
    }, [productOwnerId, actualProductOwnerId, normalizedProductOwnerId, recipientId, normalizedRecipientId]);
    
    // Audio recording states
    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [audioObjects, setAudioObjects] = useState({});
    const [playingAudio, setPlayingAudio] = useState(null);
    const [audioProgress, setAudioProgress] = useState({});
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [recordingLevel, setRecordingLevel] = useState(0);
    
    // Media states
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState(null);
    
    // Message editing states
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingContent, setEditingContent] = useState('');
    
    // Message popup menu states
    const [showMessageMenu, setShowMessageMenu] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [hoveredMessage, setHoveredMessage] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentUserIds, setCurrentUserIds] = useState({
        id: null,
        uuid: userId || null
    });
    const currentUserId = useMemo(() => currentUserIds.id || currentUserIds.uuid || null, [currentUserIds]);
    
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const recordingInterval = useRef(null);
    const fileInputRef = useRef(null);

    // Helper function to compare IDs (handles both UUIDs and numeric IDs)
    const isSameUser = (id1, id2) => {
        if (!id1 || !id2) return false;
        // Handle if id1 or id2 is an object with an id property
        const actualId1 = typeof id1 === 'object' ? (id1.id || id1.uuid) : id1;
        const actualId2 = typeof id2 === 'object' ? (id2.id || id2.uuid) : id2;
        return String(actualId1) === String(actualId2);
    };

    // Resolve both numeric ID and UUID for the logged-in user to normalize comparisons
    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            return;
        }

        // Try to get user from localStorage first (avoid API call)
        try {
            const userData = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
            if (userData.id || userData.uuid) {
                setCurrentUserIds({
                    id: userData.id ? String(userData.id) : null,
                    uuid: userData.uuid || userId || null
                });
                return; // Skip API call if we have the data
            }
        } catch (e) {
            // Fall through to API call
        }

        let isMounted = true;

        const loadCurrentUser = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/users/me/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (!response.ok) {
                    return;
                }

                const data = await response.json();
                if (!isMounted) {
                    return;
                }

                // Cache for future use
                localStorage.setItem('userData', JSON.stringify(data));

                setCurrentUserIds((prev) => ({
                    id: data.id ? String(data.id) : prev.id,
                    uuid: data.uuid || prev.uuid || userId || null
                }));
            } catch (error) {
                console.error('Failed to load user metadata for chatroom comparisons:', error);
            }
        };

        loadCurrentUser();

        return () => {
            isMounted = false;
        };
    }, [userId]);

    const isCurrentUser = useCallback((candidateId) => {
        if (!candidateId) {
            return false;
        }

        const comparisonIds = [userId, currentUserIds?.uuid, currentUserIds?.id]
            .filter(Boolean)
            .map(String);

        const normalizedCandidate = typeof candidateId === 'object'
            ? (candidateId.id || candidateId.uuid || candidateId.user)
            : candidateId;

        return comparisonIds.some(id => String(normalizedCandidate) === id);
    }, [currentUserIds, userId]);

    // Modern React Query hooks for messaging
    
    const {
        data: messagesData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: messagesLoading,
        refetch: refetchMessages,
        error: messagesError
    } = useMessages(conversationId, recipientId, productId, userId);

    const sendMessageMutation = useSendMessage();
    const markAsReadMutation = useMarkAsRead();
    const editMessageMutation = useEditMessage();
    const toggleReactionMutation = useToggleReaction();
    const queryClient = useQueryClient();

    // Set up real-time messaging
    useRealtimeMessages({
        conversationId,
        recipientId,
        productId,
        userId: currentUserIds.id || userId
    });

    // Message state for optimistic UI
    const [localMessages, setLocalMessages] = useState([]);

    // Helper to add a temp message to UI - OPTIMIZED with useCallback
    const addTempMessage = useCallback((msg) => {
        setLocalMessages((prev) => [...prev, msg]);
    }, []);
    
    // Helper to remove a temp message (when real message is received) - OPTIMIZED
    const removeTempMessage = useCallback((tempId) => {
        setLocalMessages((prev) => prev.filter(m => m.tempId !== tempId));
    }, []);

    // Helper to update a temp message status - OPTIMIZED
    const updateTempMessageStatus = useCallback((tempId, status, realMsg) => {
        if (realMsg) {
            // Messages are now plain text - no decryption needed
            const processedMsg = realMsg;

            setLocalMessages((prev) => prev.map(m => {
                if (m.tempId !== tempId) {
                    return m;
                }

                const normalizedSender = processedMsg?.sender ?? processedMsg?.sender_id ?? m.sender ?? userId;
                const normalizedRecipient = processedMsg?.recipient ?? processedMsg?.recipient_id ?? m.recipient;

                const mergedMessage = {
                    ...m,
                    ...processedMsg,
                    sender: normalizedSender,
                    sender_uuid: processedMsg?.sender_uuid || m.sender_uuid || (normalizedSender ? String(normalizedSender) : undefined),
                    recipient: normalizedRecipient,
                    recipient_uuid: processedMsg?.recipient_uuid || m.recipient_uuid,
                    content: processedMsg?.content ?? m.content,
                    status: status || 'sent',
                    tempId: undefined,
                    _isOptimistic: false
                    };

                    return mergedMessage;
                }));
        } else {
            setLocalMessages((prev) => prev.map(m => {
                if (m.tempId === tempId) {
                    return { ...m, status };
                }
                return m;
            }));
        }
    }, [userId]);

    // Merge local optimistic messages with server messages - OPTIMIZED with useMemo
    const mergedMessages = useMemo(() => {
        // Process server messages (reverse each page for chronological order)
        const serverMessages = messagesData?.pages?.flatMap(page => {

            if (!page || !page.results) {

                return [];
            }

            // Backend sends pages with newest-first order within each page
            // For chat display, we need chronological order (oldest first)
            return [...page.results].reverse();
        }) || [];
        
        // Filter out temp messages from server messages and merge with local optimistic messages
    const filteredServerMessages = serverMessages.filter(m => !m.tempId);
    const serverMessageIds = new Set(filteredServerMessages.map(msg => msg.id).filter(Boolean).map(String));
    const serverClientIds = new Set(filteredServerMessages.map(msg => msg.client_id || msg.clientId).filter(Boolean));
    const filteredLocalMessages = localMessages.filter(msg => {
        const localId = msg.id ? String(msg.id) : null;
        const localClientId = msg.client_id || msg.clientId;
        if (localClientId && serverClientIds.has(localClientId)) return false;
        return !localId || !serverMessageIds.has(localId);
    });
    const allMessages = [...filteredServerMessages, ...filteredLocalMessages];
        
        // Sort by timestamp for chronological display
        return allMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }, [messagesData?.pages, localMessages]); // Only recompute when pages or local messages change

    // Auto-scroll to bottom on initial load and new messages
    useEffect(() => {
        if (messagesEndRef.current && !isFetchingNextPage) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [mergedMessages.length, isFetchingNextPage]);

    // Handle window scroll for loading older messages (replaced container scroll)
    useEffect(() => {
        const handleScroll = () => {
            // Load older messages when scrolling near the top of the page
            if (window.scrollY <= 300 && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Extract messages from the merged data for display (use mergedMessages for proper ordering)
    const messages = mergedMessages;

    const chatPartnerId = useMemo(() => {
        if (mergedMessages && mergedMessages.length > 0) {
            for (const message of mergedMessages) {
                const senderId = message?.sender;
                const recipientIdFromMessage = message?.recipient;

                if (senderId && !isCurrentUser(senderId)) {
                    return senderId;
                }
                if (recipientIdFromMessage && !isCurrentUser(recipientIdFromMessage)) {
                    return recipientIdFromMessage;
                }
            }
        }

        const candidateIds = [
            recipientId,
            normalizedRecipientId,
            paramRecipientUuid,
            resolvedOwnerId,
            actualProductOwnerId,
            productDetails?.owner?.id,
            productDetails?.owner?.uuid
        ];

        for (const candidate of candidateIds) {
            if (candidate && !isCurrentUser(candidate)) {
                return candidate;
            }
        }

        return null;
    }, [
        mergedMessages,
        userId,
        recipientId,
        normalizedRecipientId,
        paramRecipientUuid,
        resolvedOwnerId,
        actualProductOwnerId,
        productDetails,
        isCurrentUser
    ]);

    const chatPartnerDetails = useMemo(() => {
        if (!chatPartnerId) {
            return null;
        }

        if (userDetails[chatPartnerId]) {
            return userDetails[chatPartnerId];
        }

        const matchingEntry = Object.values(userDetails).find(detail => {
            if (!detail) return false;
            return (
                isSameUser(detail.id, chatPartnerId) ||
                (detail.uuid && isSameUser(detail.uuid, chatPartnerId))
            );
        });

        return matchingEntry || null;
    }, [chatPartnerId, userDetails]);

    const profileTargetId = useMemo(() => {
        const candidates = [
            chatPartnerDetails?.uuid,
            chatPartnerDetails?.id,
            chatPartnerId,
            (!isCurrentUser(resolvedOwnerId) ? resolvedOwnerId : null),
            (!isCurrentUser(actualProductOwnerId) ? actualProductOwnerId : null),
            productDetails?.owner?.uuid,
            productDetails?.owner?.id,
            paramRecipientUuid
        ];

        return candidates.find(candidate => candidate && !isCurrentUser(candidate)) || null;
    }, [
        chatPartnerDetails,
        chatPartnerId,
        resolvedOwnerId,
        actualProductOwnerId,
        productDetails,
        paramRecipientUuid,
        userId,
        isCurrentUser
    ]);
    
    // Initialize conversation search
    const conversationSearch = useConversationSearch(messages, userId);
    const {
        searchQuery,
        isSearchActive,
        filteredMessages,
        searchMatches,
        currentMatchMessage,
        searchStats,
        searchFilters,
        searchHistory,
        toggleSearch,
        clearSearch,
        updateFilter,
        navigateToNext,
        navigateToPrevious,
        navigateToMatch,
        highlightSearchTerm,
        searchInputRef,
        setSearchQuery
    } = conversationSearch;
    
    // Initialize encryption service on component mount
    useEffect(() => {
        // Encryption service is now a no-op, but call for backwards compatibility
        encryptionService.initialize();
    }, []); // Run once on mount
    
    // OPTIMIZATION: Extract user details from messages to avoid additional API calls
    useEffect(() => {
        if (messagesData?.pages) {
            const extractedUserDetails = {};
            
            messagesData.pages.forEach(page => {
                if (page.results) {
                    page.results.forEach(message => {
                        // Extract sender details
                        if (message.sender_details && message.sender) {
                            extractedUserDetails[message.sender] = {
                                id: message.sender_details.id,
                                uuid: message.sender_details.uuid,
                                username: message.sender_details.username,
                                first_name: message.sender_details.first_name,
                                last_name: message.sender_details.last_name,
                                profile_picture: message.sender_details.profile_picture,
                            };
                        }
                        
                        // Extract recipient details  
                        if (message.recipient_details && message.recipient) {
                            extractedUserDetails[message.recipient] = {
                                id: message.recipient_details.id,
                                uuid: message.recipient_details.uuid,
                                username: message.recipient_details.username,
                                first_name: message.recipient_details.first_name,
                                last_name: message.recipient_details.last_name,
                                profile_picture: message.recipient_details.profile_picture,
                            };
                        }
                    });
                }
            });
            
            // Update user details state with extracted data
            if (Object.keys(extractedUserDetails).length > 0) {
                setUserDetails(prevDetails => ({ ...prevDetails, ...extractedUserDetails }));
            }
        }
    }, [messagesData]);

    useEffect(() => {
        if (!messagesData?.pages || messagesData.pages.length === 0) return;

        const metadataPage = messagesData.pages.find(page => page?.users && Object.keys(page.users).length > 0);
        if (!metadataPage) return;

        const participantsMap = metadataPage.users;
        if (participantsMap && Object.keys(participantsMap).length > 0) {
            setUserDetails(prev => {
                const updated = { ...prev };
                Object.values(participantsMap).forEach(participant => {
                    if (!participant?.id) return;
                    updated[participant.id] = {
                        id: participant.id,
                        uuid: participant.uuid,
                        username: participant.username || participant.full_name || prev[participant.id]?.username || '',
                        first_name: participant.first_name || participant.full_name?.split(' ')[0] || prev[participant.id]?.first_name || '',
                        last_name: participant.last_name || participant.full_name?.split(' ').slice(1).join(' ') || prev[participant.id]?.last_name || '',
                        profile_picture: participant.profile_picture || prev[participant.id]?.profile_picture || '',
                        mobile_number: participant.mobile_number || participant.phone || prev[participant.id]?.mobile_number || ''
                    };
                });
                return updated;
            });

            const otherParticipant = Object.values(participantsMap).find(participant => {
                if (!participant?.id || !userId) return false;
                return participant.id.toString() !== userId.toString();
            });

            if (otherParticipant) {
                const otherParticipantId = otherParticipant.uuid || otherParticipant.id?.toString() || null;

                if (otherParticipantId) {
                    setRecipientId(prev => {
                        if (!prev || isCurrentUser(prev)) {
                            return otherParticipantId;
                        }
                        return prev;
                    });
                }

                setActualProductOwnerId(prev => prev || otherParticipant.id);
            }
        }

        if (metadataPage.product && (!productDetailsLoaded || !productDetails)) {
            const productInfo = metadataPage.product;
            setProductName(productInfo.name || productName);
            setProductPrice(productInfo.price || productPrice);
            setProductCurrency(productInfo.currency || productCurrency || 'CDF');
            setProductDetails(productInfo);
            setProductDetailsLoaded(true);
        }
    }, [messagesData?.pages, userId, productDetailsLoaded, productDetails, productName, productPrice, productCurrency, isCurrentUser]);

    const assignColors = (users) => {
        const colors = ['#003366', '#FF6F61', '#6B4226', '#2A9D8F', '#264653'];
        const userColorsMap = {};

        users.forEach((user) => {
            if (!userColorsMap[user.id]) {
                const assignedColors = Object.values(userColorsMap);
                const availableColors = colors.filter(color => !assignedColors.includes(color));
                userColorsMap[user.id] = availableColors[0] || colors[0];
            }
        });

        setUserColors(userColorsMap);
        return userColorsMap;
    };

    // Function to fetch product details by ID
    const fetchProductDetails = async (productId) => {
        const token = Cookies.get('authToken');
        if (!token) return null;

        try {

            const response = await axios.get(`${config.backendUrl}/products/view/${productId}/`, {
                headers: { Authorization: `Token ${token}` },
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching product details:', error);
            return null;
        }
    };

    // Initialize component
    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            onLoginClick();
            return;
        }

        const fallbackOwnerId = normalizedProductOwnerId || productOwnerId || null;
        const fallbackRecipientId = normalizedRecipientId || fallbackOwnerId;
        const contactIdFromParams = !isCurrentUser(fallbackRecipientId)
            ? fallbackRecipientId
            : (!isCurrentUser(fallbackOwnerId) ? fallbackOwnerId : null);
        const normalizedContactId = contactIdFromParams ? contactIdFromParams.toString() : null;

        if (normalizedContactId) {
            setRecipientId(prev => {
                if (!prev || isCurrentUser(prev)) {
                    return normalizedContactId;
                }
                return prev;
            });
        }

        if (fallbackOwnerId && !actualProductOwnerId) {
            setActualProductOwnerId(fallbackOwnerId);
        }
        
        // Initialize userDetails from URL params if available and they represent the other user
        if (paramOwnerName && normalizedContactId && !isCurrentUser(normalizedContactId)) {
            const nameParts = paramOwnerName.split(' ');
            setUserDetails(prev => ({
                ...prev,
                [normalizedContactId]: {
                    first_name: nameParts[0] || '',
                    last_name: nameParts.slice(1).join(' ') || '',
                    mobile_number: (paramOwnerPhone && paramOwnerPhone !== 'null' && paramOwnerPhone !== 'undefined') ? paramOwnerPhone : '',
                    profile_picture: paramOwnerProfilePicture || '',
                    id: normalizedContactId,
                    username: prev?.[normalizedContactId]?.username || '',
                }
            }));
        }

        return () => {
            sessionStorage.removeItem(`chatColors_${conversationId}`);
        };
    }, [
        onLoginClick,
        normalizedProductOwnerId,
        normalizedRecipientId,
        productOwnerId,
        actualProductOwnerId,
        conversationId,
        paramOwnerName,
        paramOwnerPhone,
        paramOwnerProfilePicture,
        userId,
        isCurrentUser
    ]); // Minimal dependencies

    // Fetch product details immediately if we have productId but no product details
    useEffect(() => {
        const loadProductDetails = async () => {
            if (productId && !productDetailsLoaded && !productName && !paramProductName) {

                const productDetails = await fetchProductDetails(productId);
                if (productDetails) {
                    setProductName(productDetails.name);
                    setProductPrice(productDetails.price);
                    setProductCurrency(productDetails.currency);
                    setProductDetails(productDetails);
                    setProductDetailsLoaded(true);
                    
                    // Set owner information
                    if (productDetails.owner) {
                        setUserDetails(prev => ({
                            ...prev,
                            [productDetails.owner]: {
                                first_name: productDetails.owner_name?.split(' ')[0] || '',
                                last_name: productDetails.owner_name?.split(' ')[1] || '',
                                mobile_number: productDetails.owner_mobile_number,
                                username: productDetails.owner_username,
                                email: productDetails.owner_email,
                                id: productDetails.owner
                            }
                        }));
                        setActualProductOwnerId(productDetails.owner);
                        if (!recipientId) {
                            setRecipientId(productDetails.owner);
                        }
                    }

                }
            }
        };
        
        loadProductDetails();
    }, [productId, productDetailsLoaded, productName, paramProductName, recipientId]);

    // --- WebSocket connection for real-time messaging ---
    useEffect(() => {
        // CRITICAL FIX: Ensure we're using the actual user ID, not conversation ID
        // conversation IDs are UUIDs, user IDs can be numeric or UUID
        const wsUserId = userId;
        
        // Validate that wsUserId is not a conversation UUID 
        // Conversation UUIDs follow pattern like c8f320dc-54cf-41b1-9468-89ae48a6cde3
        // If it looks like a UUID and we have currentUserIds available, use those instead
        const looksLikeConversationId = wsUserId && wsUserId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
        
        let actualUserId = wsUserId;
        if (looksLikeConversationId && currentUserIds) {
            console.warn('⚠️  Detected conversation UUID as userId, using currentUserIds instead:', wsUserId);
            // Try to use the correct user ID from currentUserIds
            actualUserId = currentUserIds.id || currentUserIds.uuid || wsUserId;
        }
        
        if (!actualUserId) return;
        const token = Cookies.get('authToken');
        
        console.log('🔌 Connecting WebSocket with user ID:', actualUserId);
        // Prefer the conversation socket because normal REST sends broadcast to chat_<conversationId>.
        const wsPath = conversationId
            ? `chat/${conversationId}/${actualUserId}/`
            : `messages/${actualUserId}/`;
        const wsUrl = `${config.websocketUrl}${wsPath}${token ? `?token=${token}` : ''}`;

        modernMessagingService.connectWebSocket(wsUrl);
        return () => {

            modernMessagingService.disconnectWebSocket();
        };
    }, [userId, currentUserIds, conversationId]); // Add currentUserIds as dependency

    // --- Real-time new messages via WebSocket ---
    useEffect(() => {
        const handleNewMessage = (event) => {
            const newMessage = event.detail;
            
            // When a new message arrives via WebSocket, remove matching temp messages
            if (newMessage && newMessage.id) {
                // Remove temp messages that match this new message
                setLocalMessages((prev) => {
                    return prev.filter(tempMsg => {
                        // Don't remove if it's not a temp message
                        if (!tempMsg.tempId) return true;

                        const clientIdMatches = tempMsg.client_id && newMessage.client_id && tempMsg.client_id === newMessage.client_id;
                        if (clientIdMatches) return false;
                        
                        // Check if this temp message matches the new server message
                        const contentMatches = tempMsg.content?.trim() === newMessage.content?.trim();
                        const senderMatches = String(tempMsg.sender) === String(newMessage.sender || newMessage.sender_details?.id);
                        const timestampClose = Math.abs(
                            new Date(tempMsg.timestamp).getTime() - new Date(newMessage.timestamp).getTime()
                        ) < 60000; // Within 60 seconds
                        
                        // Remove temp if it matches the server message
                        if (contentMatches && senderMatches && timestampClose) {
                            return false; // Remove this temp message
                        }
                        
                        return true; // Keep this temp message
                    });
                });
                
                // Also invalidate and refetch messages to ensure the new message appears
                if (newMessage.conversation && !conversationId) {
                    setConversationId(String(newMessage.conversation));
                }
            }
        };

        // Listen for new messages from WebSocket
        window.addEventListener('newMessage', handleNewMessage);
        
        return () => {
            window.removeEventListener('newMessage', handleNewMessage);
        };
    }, [conversationId, queryClient]);

    // --- Real-time reaction updates via WebSocket ---
    useEffect(() => {
        const handleReactionUpdate = (event) => {
            const reactionData = event.detail;

            // Update the query cache immediately
            if (reactionData.message_id) {
                const queries = queryClient.getQueriesData({ queryKey: ['messages'] });
                
                queries.forEach(([queryKey, data]) => {
                    if (data?.pages) {
                        const newPages = data.pages.map(page => ({
                            ...page,
                            results: page.results.map((msg) => {
                                if (msg.id === reactionData.message_id) {
                                    return {
                                        ...msg,
                                        reaction_summary: reactionData.reaction_summary || {},
                                        user_reaction: reactionData.user_reaction || null
                                    };
                                }
                                return msg;
                            })
                        }));
                        
                        queryClient.setQueryData(queryKey, { ...data, pages: newPages });
                    }
                });
            }
        };

        // Listen for reaction updates from WebSocket
        window.addEventListener('reactionUpdate', handleReactionUpdate);
        
        return () => {
            window.removeEventListener('reactionUpdate', handleReactionUpdate);
        };
    }, [queryClient]);

    // Mark messages as read when conversation is viewed
    useEffect(() => {
        if (!conversationId || !markAsReadMutation || messagesLoading) return;
        
        // Get unread messages that the current user should mark as read
        const unreadMessages = mergedMessages.filter(msg => 
            !msg.is_read && 
            (msg.recipient_id || msg.recipient)?.toString() === (currentUserIds.id || userId)?.toString() &&
            !msg.tempId // Don't mark temporary messages
        );
        
        if (unreadMessages.length > 0) {

            // Mark the entire conversation as read (simpler than individual messages)
            markAsReadMutation.mutate({ 
                conversationId: parseInt(conversationId) 
            }, {
                onSuccess: () => {

                },
                onError: (error) => {
                    console.error('Failed to mark conversation as read:', error);
                }
            });
        }
    }, [conversationId, mergedMessages, markAsReadMutation, messagesLoading, userId, currentUserIds.id]);

    const handleReplyChange = (event) => {
        setReplyContent(event.target.value);
    };

    // Refactored send handler
    const handleReplySubmit = async () => {
        if (!replyContent.trim() && attachments.length === 0) return;
        const token = Cookies.get('authToken');
        if (!token) {
            onLoginClick();
            return;
        }
        const senderId = userId; // Keep as-is (can be UUID or ID)
        
        // CRITICAL FIX: Ensure senderId is not a conversation UUID
        let actualSenderId = senderId;
        const looksLikeConversationId = senderId && senderId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
        if (looksLikeConversationId && currentUserIds) {
            console.warn('⚠️  Detected conversation UUID as senderId, using currentUserIds instead:', senderId);
            actualSenderId = currentUserIds.id || currentUserIds.uuid || senderId;
        }
        
    const recipId = recipientId || actualProductOwnerId || normalizedProductOwnerId || productOwnerId; // Keep as-is
        const prodId = productId; // Keep as-is
        if (!actualSenderId || !recipId || !prodId) {
            setNotification({ message: `Données invalides pour l'envoi du message.`, type: 'error', header: 'Erreur' });
            return;
        }
        // Create temp message for UI
        const tempId = `temp_${Date.now()}`;
        const clientId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const tempMessage = {
            id: tempId,
            tempId,
            client_id: clientId,
            content: replyContent.trim(),
            timestamp: new Date().toISOString(),
            sender: actualSenderId,
            recipient: recipId,
            is_read: false,
            product: prodId,
            status: 'sending',
            attachments,
        };
        addTempMessage(tempMessage);
        setReplyContent('');
        setAttachments([]);
        scrollToBottom();
        // Prepare messageData for backend
        const messageData = {
            content: replyContent.trim(),
            recipient: recipId,
            product: prodId,
            product_id: prodId,
            sender: actualSenderId,
            sender_id: actualSenderId,
            client_id: clientId,
            message_type: 'text',
            attachments: attachments.map(att => att.originalFile).filter(Boolean) // Extract actual File objects
        };
        try {
            const result = await sendMessageMutation.mutateAsync(messageData);

            updateTempMessageStatus(tempId, 'sent', result);
            if (result?.conversation && !conversationId) {
                setConversationId(String(result.conversation));
            }
            refetchMessages();
        } catch (error) {
            console.error('REST send failed:', error);
            updateTempMessageStatus(tempId, 'failed');
            setNotification({ message: 'Échec de l\'envoi du message.', type: 'error', header: 'Erreur' });
        }
    };

    // Get mutation status for UI feedback
    const isSending = sendMessageMutation.isPending;
    const sendError = sendMessageMutation.error;

    // Retry failed message
    const handleRetryMessage = async (failedMessage) => {
        if (!failedMessage.tempId) return;
        
        updateTempMessageStatus(failedMessage.tempId, 'sending');
        
        const messageData = {
            content: failedMessage.content,
            recipient: failedMessage.recipient,
            product: failedMessage.product,
            product_id: failedMessage.product,
            sender: failedMessage.sender,
            sender_id: failedMessage.sender,
            client_id: failedMessage.client_id || `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
            message_type: 'text',
            attachments: failedMessage.attachments || []
        };

        try {
            await sendMessageMutation.mutateAsync(messageData);
            updateTempMessageStatus(failedMessage.tempId, 'sent');
        } catch (error) {
            updateTempMessageStatus(failedMessage.tempId, 'failed');
            setNotification({ message: 'Échec de l\'envoi du message.', type: 'error', header: 'Erreur' });
        }
    };

    // Enhanced Audio recording functions
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                } 
            });
            
            // Create MediaRecorder with better quality
            const recorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus' // Better audio quality
            });
            
            const chunks = [];
            setAudioChunks([]);
            
            // Set up audio level monitoring
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(stream);
            const analyzer = audioContext.createAnalyser();
            analyzer.fftSize = 256;
            source.connect(analyzer);
            
            const dataArray = new Uint8Array(analyzer.frequencyBinCount);
            
            const updateLevel = () => {
                if (isRecording) {
                    analyzer.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((sum, value) => sum + value) / dataArray.length;
                    setRecordingLevel(average / 255 * 100); // Convert to percentage
                    requestAnimationFrame(updateLevel);
                }
            };
            
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                    setAudioChunks(prev => [...prev, event.data]);
                }
            };
            
            recorder.onstop = async () => {
                const blob = new Blob(chunks, { type: 'audio/webm' });
                
                // Stop all tracks to release microphone
                stream.getTracks().forEach(track => track.stop());
                audioContext.close();
                
                if (chunks.length > 0 && recordingDuration > 0) {
                    await sendAudioMessage(blob, recordingDuration);
                }
                
                // Reset states
                setIsRecording(false);
                setRecordingDuration(0);
                setRecordingLevel(0);
                setMediaRecorder(null);
            };
            
            // Start recording
            recorder.start(100); // Collect data every 100ms
            setMediaRecorder(recorder);
            setIsRecording(true);
            setRecordingDuration(0);
            updateLevel();
            
            // Start duration counter
            recordingInterval.current = setInterval(() => {
                setRecordingDuration(prev => {
                    const newDuration = prev + 1;
                    if (newDuration >= 300) { // Max 5 minutes
                        stopRecording();
                        return 300;
                    }
                    return newDuration;
                });
            }, 1000);

        } catch (error) {
            console.error('Error starting recording:', error);
            setNotification({ 
                message: 'Impossible d\'accéder au microphone. Vérifiez les permissions.', 
                type: 'error', 
                header: 'Erreur' 
            });
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
        
        if (recordingInterval.current) {
            clearInterval(recordingInterval.current);
        }
        
        setIsRecording(false);
        setRecordingLevel(0);
    };

    const cancelRecording = () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
        
        if (recordingInterval.current) {
            clearInterval(recordingInterval.current);
        }
        
        setIsRecording(false);
        setRecordingDuration(0);
        setRecordingLevel(0);
        setMediaRecorder(null);
        setAudioChunks([]);
    };

    const sendAudioMessage = async (audioBlob, duration) => {
        try {

            // Detect if userId is a conversation UUID and use current user ID instead
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId);
            const actualSenderId = isUUID ? (currentUserId || currentUserIds?.id || currentUserIds?.uuid || userId) : userId;
            
            const senderId = actualSenderId;
            const recipId = recipientId || actualProductOwnerId || normalizedProductOwnerId || productOwnerId; // Keep as-is
            const prodId = productId; // Keep as-is

            // Create temporary message for UI
            const tempId = `temp_${Date.now()}`;
            const clientId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
            const audioUrl = URL.createObjectURL(audioBlob);
            
            const tempMessage = {
                id: tempId,
                tempId,
                client_id: clientId,
                content: '',
                audio: audioUrl,
                audio_duration: duration,
                timestamp: new Date().toISOString(),
                sender: senderId,
                recipient: recipId,
                is_read: false,
                product: prodId,
                status: 'sending',
                message_type: 'audio'
            };
            
            addTempMessage(tempMessage);
            scrollToBottom();

            // Prepare FormData for backend
            const formData = new FormData();
            formData.append('sender', senderId);
            formData.append('recipient', recipId);
            formData.append('product', prodId);
            formData.append('content', ''); // Empty content for audio messages
            formData.append('client_id', clientId);
            formData.append('audio', audioBlob, `voice_note_${Date.now()}.webm`);
            formData.append('audio_duration', Math.round(duration));
            formData.append('message_type', 'audio');

            // Send via REST API (audio messages work better with REST than WebSocket)
            try {
                const result = await sendMessageMutation.mutateAsync({
                    formData,
                    isAudio: true
                });

                updateTempMessageStatus(tempId, 'sent', result);
                if (result?.conversation && !conversationId) {
                    setConversationId(String(result.conversation));
                }
                
                // Clean up temp message after delay
                setTimeout(() => {
                    removeTempMessage(tempId);
                    refetchMessages();
                }, 2000);
                
            } catch (error) {
                console.error('Failed to send audio message:', error);
                updateTempMessageStatus(tempId, 'failed');
                setNotification({ 
                    message: 'Échec de l\'envoi du message audio.', 
                    type: 'error', 
                    header: 'Erreur' 
                });
            }
            
        } catch (error) {
            console.error('Error preparing audio message:', error);
            setNotification({ 
                message: 'Erreur lors de la préparation du message audio.', 
                type: 'error', 
                header: 'Erreur' 
            });
        }
    };

    // Format audio URL for proper playback
    const formatAudioUrl = (audioUrl) => {
        if (!audioUrl) return '';
        
        // If it's already a blob URL (for temp messages), return as-is
        if (audioUrl.startsWith('blob:')) {
            return audioUrl;
        }
        
        // If it's a relative URL from backend, prepend backend URL
        if (audioUrl.startsWith('/') || audioUrl.startsWith('audio_messages/')) {
            return `${config.backendUrl}${audioUrl.startsWith('/') ? '' : '/'}${audioUrl}`;
        }
        
        // If it's already a full URL, return as-is
        return audioUrl;
    };

    // Media functions
    const handleMediaPick = async (type) => {
        if (!fileInputRef.current) return;
        
        fileInputRef.current.accept = type === 'image' ? 'image/*' : 'video/*';
        fileInputRef.current.click();
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        
        files.forEach(file => {
            if (attachments.length >= 3) {
                setNotification({ message: 'Maximum 3 fichiers autorisés', type: 'error', header: 'Erreur' });
                return;
            }

            // Check file size (50MB limit)
            if (file.size > 50 * 1024 * 1024) {
                setNotification({ 
                    message: `Le fichier "${file.name}" dépasse la limite de 50MB`, 
                    type: 'error', 
                    header: 'Fichier trop volumineux' 
                });
                return;
            }
            
            // Determine file type for better display
            let fileType = 'file';
            if (file.type.startsWith('image/')) {
                fileType = 'image';
            } else if (file.type.startsWith('video/')) {
                fileType = 'video';
            } else if (file.type.startsWith('audio/')) {
                fileType = 'audio';
            } else if (file.type === 'application/pdf') {
                fileType = 'pdf';
            } else if (file.type.includes('document') || file.type.includes('msword') || file.type.includes('openxmlformats-officedocument')) {
                fileType = 'document';
            }
            
            const attachment = {
                id: Date.now() + Math.random(),
                name: file.name,
                file: URL.createObjectURL(file),
                type: fileType,
                size: file.size,
                mimeType: file.type,
                originalFile: file
            };
            
            setAttachments(prev => [...prev, attachment]);
        });

        // Clear the input value so the same file can be selected again
        event.target.value = '';
    };

    // Enhanced attachment selection (for documents)
    const handleAttachmentSelect = async () => {
        try {
            if (attachments.length >= 3) {
                setNotification({ message: 'Maximum 3 fichiers autorisés', type: 'error', header: 'Erreur' });
                return;
            }

            // For web, we'll use a file input
            if (!fileInputRef.current) return;
            
            fileInputRef.current.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.csv,.rtf,.odt,.ods,.odp';
            fileInputRef.current.click();
        } catch (error) {
            console.error('Error picking document:', error);
        }
    };

    const showMediaOptionsModal = () => {
        setShowMediaOptions(true);
    };

    // Audio playback
    const handleAudioPlay = async (audioUrl) => {
        if (playingAudio === audioUrl) {
            // Pause current audio
            const audio = audioObjects[audioUrl];
            if (audio) {
                audio.pause();
                setPlayingAudio(null);
            }
        } else {
            // Play new audio
            if (playingAudio) {
                const currentAudio = audioObjects[playingAudio];
                if (currentAudio) currentAudio.pause();
            }
            
            let audio = audioObjects[audioUrl];
            if (!audio) {
                audio = new Audio(audioUrl);
                setAudioObjects(prev => ({ ...prev, [audioUrl]: audio }));
                
                audio.onended = () => setPlayingAudio(null);
                audio.ontimeupdate = () => {
                    setAudioProgress(prev => ({
                        ...prev,
                        [audioUrl]: (audio.currentTime / audio.duration) * 100
                    }));
                };
            }
            
            await audio.play();
            setPlayingAudio(audioUrl);
        }
    };

    // Format duration
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle attachment press
    const handleAttachmentPress = (attachment) => {
        if (attachment.type === 'image' || attachment.type === 'video') {
            setPreviewContent({
                type: attachment.type,
                url: attachment.file,
                name: attachment.name
            });
            setPreviewModalVisible(true);
        } else {
            // Download or open file
            const link = document.createElement('a');
            link.href = attachment.file;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleAttachmentChange = (event) => {
        handleFileSelect(event);
        const files = Array.from(event.target.files);

        const allowedFiles = files.filter(file => {
            const isAllowedType = file.type.startsWith('image/') || file.type === 'application/pdf';
            if (!isAllowedType) {
                setNotification({
                    message: 'Seules les images et fichiers PDF sont autorisés.',
                    type: 'error',
                    header: 'Erreur',
                });
            }
            return isAllowedType;
        });

        if (attachments.length + allowedFiles.length > 3) {
            setNotification({
                message: 'Maximum 3 pièces jointes autorisées.',
                type: 'error',
                header: 'Erreur',
            });
            return;
        }

        setAttachments(prev => [...prev, ...allowedFiles].slice(0, 3));
    };
    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Emoji selection is now handled inline in the emoji buttons

    const formatAttachmentUrl = (attachmentUrl) => {
        if (attachmentUrl.startsWith('http')) {
            return attachmentUrl;
        }

        if (attachmentUrl.includes('s3.amazonaws.com')) {
            return `https://${attachmentUrl.replace(/^https?:\/\//, '')}`;
        }

        return `${config.backendUrl}${attachmentUrl}`;
    };

    // Loading components
    const ProductHeaderSkeleton = () => (
        <div className="chat-header">
            <div className="chat-product">
                <div className="chat-product__name skeleton-shimmer" style={{ 
                    width: '200px', 
                    height: '20px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div className="shimmer-effect"></div>
                </div>
                <div className="chat-product__price skeleton-shimmer" style={{ 
                    width: '100px', 
                    height: '16px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px', 
                    marginTop: '8px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div className="shimmer-effect"></div>
                </div>
                <div className="skeleton-shimmer" style={{ 
                    width: '150px', 
                    height: '12px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px', 
                    marginTop: '6px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div className="shimmer-effect"></div>
                </div>
            </div>
            <div className="chat-owner">
                <div className="chat-owner__avatar skeleton-shimmer" style={{ 
                    width: '44px', 
                    height: '44px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '50%',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div className="shimmer-effect"></div>
                </div>
                <div className="skeleton-shimmer" style={{ 
                    width: '80px', 
                    height: '12px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px', 
                    marginTop: '4px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div className="shimmer-effect"></div>
                </div>
            </div>
        </div>
    );

    const MessagesSkeleton = () => (
        <ul className="messages-list">
            {[1, 2, 3].map((i) => (
                <li key={i} className={`message-item ${i % 2 === 0 ? 'message-right' : 'message-left'}`}>
                    <div className="message-box">
                        <div className="message-content-wrapper">
                            <div className="skeleton-text" style={{ 
                                width: `${Math.random() * 200 + 100}px`, 
                                height: '16px', 
                                backgroundColor: '#f0f0f0', 
                                borderRadius: '4px',
                                marginBottom: '8px'
                            }}>
                            </div>
                        </div>
                        <div className="message-meta">
                            <div className="skeleton-shimmer" style={{ 
                                width: '80px', 
                                height: '12px', 
                                backgroundColor: '#f0f0f0', 
                                borderRadius: '4px', 
                                marginTop: '4px',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div className="shimmer-effect"></div>
                            </div>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );

    // ==================
    // MESSAGE EDITING FUNCTIONS
    // ==================

    const [isEditing, setIsEditing] = useState(false);

    const startEditingMessage = (message) => {
        setEditingMessageId(message.id);
        setEditingContent(message.content);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setEditingMessageId(null);
        setEditingContent('');
        setIsEditing(false);
    };

    const confirmEditMessage = async () => {
        if (!editingMessageId || !editingContent.trim()) return;
        
        try {
            await editMessageMutation.mutateAsync({
                messageId: editingMessageId,
                newContent: editingContent
            });

            setNotification({
                message: 'Message modifié avec succès',
                type: 'success',
                header: 'Succès'
            });

            cancelEditing();
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors de la modification du message',
                type: 'error',
                header: 'Erreur'
            });
        }
    };

    const canEditMessage = (message) => {
        if (!message || !userId) return false;
        const loggedInUser = { id: userId };
        return modernMessagingService.canEditMessage(message, loggedInUser);
    };

    const canDeleteMessage = (message) => {
        if (!message || !userId) return false;
        const loggedInUser = { id: userId };
        return modernMessagingService.canDeleteMessage(message, loggedInUser);
    };

    const deleteMessage = async (messageId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
            return;
        }

        try {
            await modernMessagingService.deleteMessage(messageId);
            setNotification({
                message: 'Message supprimé avec succès',
                type: 'success',
                header: 'Succès'
            });
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors de la suppression du message',
                type: 'error',
                header: 'Erreur'
            });
        }
    };

    // ==================
    // END MESSAGE EDITING FUNCTIONS
    // ==================

    // Message menu functions
    const handleMessageRightClick = (e, message) => {
        e.preventDefault();
        setSelectedMessage(message);
        setMenuPosition({ x: e.clientX, y: e.clientY });
        setShowMessageMenu(true);
    };

    const handleMessageHover = (message) => {
        setHoveredMessage(message);
    };

    const handleMessageLeave = () => {
        setHoveredMessage(null);
    };

    const closeMessageMenu = () => {
        setShowMessageMenu(false);
        setSelectedMessage(null);
    };

    const handleQuickReaction = (message, emoji) => {
        toggleReactionMutation.mutate({ messageId: message.id, emoji });
        closeMessageMenu();
    };

    const handleReplyToMessage = (message) => {

        // Set the message we're replying to in the text area
        const replyText = `@${message.sender}: "${message.content.substring(0, 50)}${message.content.length > 50 ? '...' : ''}"

`;
        setReplyContent(replyText);
        // Focus the text area
        setTimeout(() => {
            const textarea = document.querySelector('.reply-textarea');
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        }, 100);
        
        setNotification({
            message: 'Répondre au message - tapez votre réponse ci-dessous',
            type: 'info',
            header: 'Réponse'
        });
    };

    const handleEditMessage = (message) => {
        if (isCurrentUser(message.sender)) {
            setEditingMessageId(message.id);
            setEditingContent(message.content);
            setReplyContent(message.content);

            // Focus the text area
            setTimeout(() => {
                const textarea = document.querySelector('.reply-textarea');
                if (textarea) {
                    textarea.focus();
                    textarea.select();
                }
            }, 100);
            
            setNotification({
                message: 'Mode édition activé - modifiez votre message',
                type: 'info',
                header: 'Édition'
            });
        } else {
            setNotification({
                message: 'Vous ne pouvez modifier que vos propres messages',
                type: 'error',
                header: 'Erreur'
            });
        }
    };

    const handleDeleteMessage = async (message) => {
        if (isCurrentUser(message.sender)) {
            if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.')) {
                try {
                    const token = Cookies.get('authToken');
                    const response = await axios.delete(
                        `${config.backendUrl}/messaging/messages/${message.id}/`,
                        {
                            headers: {
                                'Authorization': `Token ${token}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (response.status === 204 || response.status === 200) {
                        // Refresh messages to update the UI
                        refetchMessages();
                        setNotification({
                            message: 'Message supprimé avec succès',
                            type: 'success',
                            header: 'Succès'
                        });
                    }
                } catch (error) {
                    console.error('Error deleting message:', error);
                    setNotification({
                        message: 'Erreur lors de la suppression du message',
                        type: 'error',
                        header: 'Erreur'
                    });
                }
            }
        } else {
            setNotification({
                message: 'Vous ne pouvez supprimer que vos propres messages',
                type: 'error',
                header: 'Erreur'
            });
        }
    };

    const handleMessageOptions = (message) => {

        // Add more options logic here
        setNotification({
            message: 'Options de message en cours de développement',
            type: 'info',
            header: 'Info'
        });
    };

    const handleReportSubmit = async (reportData) => {
        try {
            // Build the frontend URL based on report target type
            let targetUrl = '';
            if (reportTarget.type === 'profile') {
                targetUrl = `${window.location.origin}/user/${reportTarget.id}`;
            } else if (reportTarget.type === 'product') {
                targetUrl = `${window.location.origin}/product/${reportTarget.id}`;
            }
            
            await moderationService.submitReport({
                target_type: reportTarget.type,
                target_id: reportTarget.id,
                reason: reportData.reason,
                details: reportData.description, // Changed from 'description' to 'details' to match backend model
                target_url: targetUrl,
            });
            
            setShowReportModal(false);
            setNotification({
                message: 'Votre signalement a été envoyé avec succès. Notre équipe l\'examinera dans un délai de 24 heures.',
                type: 'success',
                header: 'Signalement envoyé',
            });
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors de l\'envoi du signalement. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur',
            });
        }
    };

    const handleReportUser = async () => {
        const targetId = chatPartnerDetails.uuid || chatPartnerDetails.id;
        const targetName = `${chatPartnerDetails.first_name || ''} ${chatPartnerDetails.last_name || ''}`.trim() || 'Utilisateur';
        
        try {
            const result = await moderationService.checkExistingReport('profile', targetId);
            
            if (result.exists) {
                const reportDate = new Date(result.report.created_at).toLocaleDateString('fr-FR');
                setNotification({
                    message: `Vous avez déjà signalé cet utilisateur le ${reportDate}. Notre équipe examinera votre signalement dans un délai de 24 heures.`,
                    type: 'info',
                    header: 'Signalement déjà envoyé',
                });
            } else {
                setReportTarget({
                    type: 'profile',
                    id: targetId,
                    name: targetName
                });
                setShowReportModal(true);
            }
        } catch (error) {
            console.error('Error checking existing report:', error);
            setReportTarget({
                type: 'profile',
                id: targetId,
                name: targetName
            });
            setShowReportModal(true);
        }
    };

    // Utility function to format date separators
    const getDateString = (timestamp) => {
        const messageDate = moment(timestamp);
        const today = moment();
        const yesterday = moment().subtract(1, 'day');
        
        if (messageDate.isSame(today, 'day')) {
            return "Aujourd'hui";
        } else if (messageDate.isSame(yesterday, 'day')) {
            return "Hier";
        } else if (messageDate.isSame(today, 'year')) {
            return messageDate.format('dddd DD MMMM');
        } else {
            return messageDate.format('dddd DD MMMM YYYY');
        }
    };

    // Function to check if date separator should be shown
    const shouldShowDateSeparator = (currentMessage, previousMessage) => {
        if (!previousMessage) return true;
        return !moment(currentMessage.timestamp).isSame(previousMessage.timestamp, 'day');
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showMessageMenu) {
                closeMessageMenu();
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showMessageMenu]);

    // Keyboard shortcuts for search
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl+F or Cmd+F to open search
            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault();
                if (!isSearchActive) {
                    toggleSearch();
                }
                return;
            }
            
            // Escape to close search
            if (e.key === 'Escape' && isSearchActive) {
                toggleSearch();
                return;
            }
            
            // F3 or Ctrl+G for next match
            if ((e.key === 'F3' || ((e.ctrlKey || e.metaKey) && e.key === 'g')) && isSearchActive) {
                e.preventDefault();
                if (e.shiftKey) {
                    navigateToPrevious();
                } else {
                    navigateToNext();
                }
                return;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isSearchActive, toggleSearch, navigateToNext, navigateToPrevious]);

    // Access Redux product grid for product details in chat header or message previews
    const reduxProducts = useSelector(state => state.productGrid.products);

    return (
        <div className="wzc-container">
            <ChatHeader
                productDetailsLoaded={productDetailsLoaded}
                productName={productName}
                productPrice={productPrice}
                productCurrency={productCurrency}
                productId={productId}
                chatPartnerDetails={chatPartnerDetails}
                chatPartnerId={chatPartnerId}
                userColors={userColors}
                profileTargetId={profileTargetId}
                isCurrentUser={isCurrentUser}
                onReportUser={handleReportUser}
            />

            {/* Conversation Search Component */}
            <ConversationSearch
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isSearchActive={isSearchActive}
                toggleSearch={toggleSearch}
                clearSearch={clearSearch}
                searchStats={searchStats}
                searchFilters={searchFilters}
                updateFilter={updateFilter}
                searchHistory={searchHistory}
                navigateToNext={navigateToNext}
                navigateToPrevious={navigateToPrevious}
                searchInputRef={searchInputRef}
                currentMatchMessage={currentMatchMessage}
                onNavigateToMessage={(message) => {
                    // Scroll to the specific message
                    const messageElement = document.querySelector(`[data-message-id="${message.id}"]`);
                    if (messageElement) {
                        messageElement.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'center'
                        });
                        // Highlight the message temporarily
                        messageElement.classList.add('message-highlighted');
                        setTimeout(() => {
                            messageElement.classList.remove('message-highlighted');
                        }, 2000);
                    }
                }}
            />
            
            {messagesLoading ? (
                <div>
                    {productDetailsLoaded && (
                        <div className="loading-indicator" style={{ 
                            padding: '20px', 
                            textAlign: 'center', 
                            borderTop: '1px solid #e5e7eb' 
                        }}>
                            <span>Chargement des messages</span>
                            <div className="loading-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    )}
                    <MessagesSkeleton />
                </div>
            ) : (
                <div className="wzc-messages-container"
                    ref={messagesContainerRef}
                >
                    {isFetchingNextPage && (
                        <div style={{ textAlign: 'center', padding: '10px' }}>
                            <div className="loading-indicator">
                                Chargement des messages précédents...
                            </div>
                        </div>
                    )}
                    <ul className="wzc-messages-list">
                        {(isSearchActive ? filteredMessages : messages).length === 0 ? (
                        <div style={{ 
                            padding: '40px', 
                            textAlign: 'center', 
                            color: '#6b7280',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            {isSearchActive ? (
                                <div>
                                    <p>No messages found for "{searchQuery}"</p>
                                    <p><small>Try adjusting your search terms or filters.</small></p>
                                    <button onClick={clearSearch} style={{ marginTop: '8px' }}>
                                        Clear search
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p>Aucun message pour le moment.</p>
                                    <p><small>Commencez la conversation en envoyant un message ci-dessous.</small></p>
                                </>
                            )}
                        </div>
                    ) : (
                        // Only render messages when userId is available to prevent positioning issues
                        !userId ? (
                            <div style={{ 
                                padding: '40px', 
                                textAlign: 'center', 
                                color: '#6b7280',
                                borderTop: '1px solid #e5e7eb'
                            }}>
                                <p>Loading messages...</p>
                            </div>
                        ) : (
                            (isSearchActive ? filteredMessages : messages).map((message, index) => {
                                const isCurrentMatch = currentMatchMessage?.id === message.id;
                                // Use UUID if available, fallback to numeric ID
                                const messageSenderId = message.sender_uuid || message.sender;
                                const isOwnMessage = isCurrentUser(messageSenderId);
                                
                                // Debug logging (temporary)
                                if (index === 0) {
                                    console.log('Debug message positioning:', {
                                        messageSender: message.sender,
                                        messageSenderUuid: message.sender_uuid,
                                        messageSenderId: messageSenderId,
                                        userId: userId,
                                        isOwnMessage: isOwnMessage,
                                        messageId: message.id
                                    });
                                }
                                
                                // Get previous message for date separator
                                const messagesArray = isSearchActive ? filteredMessages : messages;
                                const previousMessage = index > 0 ? messagesArray[index - 1] : null;
                                const showDateSeparator = shouldShowDateSeparator(message, previousMessage);
                                
                                return (
                                    <React.Fragment key={message.id || message.tempId}>
                                        {/* Date Separator */}
                                        {showDateSeparator && (
                                            <div className="date-separator">
                                                <div className="date-separator-line"></div>
                                                <span className="date-separator-text">
                                                    {getDateString(message.timestamp)}
                                                </span>
                                                <div className="date-separator-line"></div>
                                            </div>
                                        )}
                                        
                                        {/* Message */}
                        <li
                            data-message-id={message.id}
                            className={`wzc-msg-item ${isOwnMessage ? 'wzc-msg-sent' : 'wzc-msg-received'} ${hoveredMessage?.id === message.id ? 'wzc-msg-hovered' : ''} ${isCurrentMatch ? 'current-search-match' : ''}`}
                            onMouseEnter={() => handleMessageHover(message)}
                            onMouseLeave={handleMessageLeave}
                            onContextMenu={(e) => handleMessageRightClick(e, message)}
                        >
                            <div className="wzc-msg-bubble" style={{ borderColor: userColors[message.sender] || '#003366' }}>
                                {/* Message hover actions */}
                                {hoveredMessage?.id === message.id && (
                                    <div className="wzc-msg-hover-actions">
                                        {/* Only show reaction button for other participant's messages */}
                                        {!isOwnMessage && (
                                            <button 
                                                className="wzc-hover-btn wzc-hover-reaction"
                                                onClick={() => {
                                                    setSelectedMessage(message);
                                                    setShowMessageMenu(true);
                                                }}
                                                title="Ajouter une réaction"
                                            >
                                                😊
                                            </button>
                                        )}
                                        <button 
                                            className="wzc-hover-btn wzc-hover-reply"
                                            onClick={() => handleReplyToMessage(message)}
                                            title="Répondre"
                                        >
                                        </button>
                                        {isOwnMessage && (
                                            <>
                                                <button 
                                                    className="wzc-hover-btn wzc-hover-edit"
                                                    onClick={() => handleEditMessage(message)}
                                                    title="Modifier"
                                                >
                                                    ✏️
                                                </button>
                                                <button 
                                                    className="wzc-hover-btn wzc-hover-delete"
                                                    onClick={() => handleDeleteMessage(message)}
                                                    title="Supprimer"
                                                >
                                                    🗑️
                                                </button>
                                            </>
                                        )}
                                        <button 
                                            className="wzc-hover-btn wzc-hover-more"
                                            onClick={(e) => {
                                                const rect = e.target.getBoundingClientRect();
                                                setMenuPosition({ x: rect.left, y: rect.bottom });
                                                setSelectedMessage(message);
                                                setShowMessageMenu(true);
                                            }}
                                            title="Plus d'actions"
                                        >
                                            ⋯
                                        </button>
                                    </div>
                                )}
                                <div className="wzc-msg-content-wrap">
                                    {/* Text content */}
                                    {message.content && (
                                        <div 
                                            className="wzc-msg-content"
                                            dangerouslySetInnerHTML={{
                                                __html: isSearchActive && searchQuery ? 
                                                    highlightSearchTerm(message.content, isCurrentMatch) : 
                                                    message.content
                                            }}
                                        />
                                    )}
                                    
                                    {/* Audio message */}
                                    {message.audio && (
                                        <div className={`wzc-audio-message ${isCurrentUser(message.sender) ? 'sent' : 'received'}`}>
                                            <button 
                                                className="wzc-audio-play-btn"
                                                onClick={() => handleAudioPlay(formatAudioUrl(message.audio))}
                                                title={playingAudio === message.audio ? 'Pause' : 'Play'}
                                            >
                                                {playingAudio === formatAudioUrl(message.audio) ? '⏸️' : '▶️'}
                                            </button>
                                            <div className="wzc-audio-info">
                                                <div className="wzc-audio-waveform">
                                                    <div 
                                                        className="wzc-audio-progress"
                                                        style={{ width: `${audioProgress[formatAudioUrl(message.audio)] || 0}%` }}
                                                    />
                                                </div>
                                                <span className="wzc-audio-duration">
                                                    🎵 {formatDuration(message.audio_duration || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Attachments */}
                                    {message.attachments && message.attachments.length > 0 &&
                                        message.attachments.map((attachment, index) => (
                                            <div key={index} className="wzc-attachment-wrap">
                                                {attachment.type === 'image' ? (
                                                    <img 
                                                        src={typeof attachment.file === 'string' ? attachment.file : URL.createObjectURL(attachment.originalFile)}
                                                        alt={attachment.name}
                                                        className="wzc-attachment-image"
                                                        onClick={() => handleAttachmentPress(attachment)}
                                                        style={{ maxWidth: '200px', maxHeight: '200px', cursor: 'pointer' }}
                                                    />
                                                ) : attachment.type === 'video' ? (
                                                    <video 
                                                        src={typeof attachment.file === 'string' ? attachment.file : URL.createObjectURL(attachment.originalFile)}
                                                        className="wzc-attachment-video"
                                                        controls
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                    />
                                                ) : (
                                                    <div className="wzc-attachment-file" onClick={() => handleAttachmentPress(attachment)}>
                                                        <span className="wzc-file-icon">📄</span>
                                                        <span className="wzc-file-name">{attachment.name}</span>
                                                        <button className="wzc-download-btn">
                                                            <FaDownload />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                </div>                                        <div className="wzc-msg-meta">
                                            <div className="wzc-msg-info">
                                                <span className="wzc-msg-time">
                                                    {moment(message.timestamp).format('LLL')}
                                                    {message.is_edited && <span className="wzc-edited-tag"> (modifié)</span>}
                                                </span>
                                                
                                                {/* Edit/Delete buttons for own messages */}
                                                {isCurrentUser(message.sender) && !message._isOptimistic && (
                                                    <div className="wzc-msg-actions">
                                                        {canEditMessage(message) && (
                                                            <button 
                                                                className="wzc-action-btn wzc-edit-btn"
                                                                onClick={() => startEditingMessage(message)}
                                                                title="Modifier le message"
                                                            >
                                                                ✏️
                                                            </button>
                                                        )}
                                                        {canDeleteMessage(message) && (
                                                            <button 
                                                                className="wzc-action-btn wzc-delete-btn"
                                                                onClick={() => deleteMessage(message.id)}
                                                                title="Supprimer le message"
                                                            >
                                                                🗑️
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="wzc-msg-status-wrap">
                                                {isCurrentUser(message.sender) && (
                                                    <>
                                                        {message._isOptimistic || message.status === 'sending' ? (
                                                            <span className="wzc-msg-status wzc-status-sending">⏳ Envoi...</span>
                                                        ) : message.status === 'failed' ? (
                                                            <span className="wzc-msg-status wzc-status-failed" onClick={() => handleRetryMessage(message)}>
                                                                ⟳ Réessayer
                                                            </span>
                                                        ) : message.is_read ? (
                                                            <span className="wzc-msg-status wzc-status-read">✓✓</span>
                                                        ) : (
                                                            <span className="wzc-msg-status wzc-status-sent">✓</span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                        {/* Message Reactions - only show on other participant's messages */}
                        {message.sender !== parseInt(userId) && (
                            <MessageReactions 
                                message={message}
                                currentUserId={parseInt(userId)}
                                isOwnMessage={false}
                            />
                        )}
                    </div>
                </li>
                                    </React.Fragment>
                                );
            })
        )
    )}
            <div ref={messagesEndRef} />
        </ul>
        </div>
    )}

            <MessageInput
                replyContent={replyContent}
                onReplyChange={handleReplyChange}
                onReplySubmit={handleReplySubmit}
                attachments={attachments}
                onAddAttachment={handleFileSelect}
                onRemoveAttachment={removeAttachment}
                showEmojiPicker={showEmojiPicker}
                onToggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
                onEmojiSelect={(emoji) => {
                    setReplyContent(prev => prev + emoji);
                    setShowEmojiPicker(false);
                }}
                showMediaOptions={showMediaOptions}
                onToggleMediaOptions={showMediaOptionsModal}
                isRecording={isRecording}
                recordingDuration={recordingDuration}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                onCancelRecording={cancelRecording}
                recordingLevel={recordingLevel}
                disabled={isSending}
            />

            {/* Media Preview Modal */}
            {previewModalVisible && previewContent && (
                <div className="wzc-preview-modal" onClick={() => setPreviewModalVisible(false)}>
                    <div className="wzc-preview-content" onClick={(e) => e.stopPropagation()}>
                        {previewContent.type === 'image' ? (
                            <img src={previewContent.url} alt={previewContent.name} style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
                        ) : previewContent.type === 'video' ? (
                            <video src={previewContent.url} controls style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
                        ) : (
                            <div>Aperçu non disponible pour ce type de fichier</div>
                        )}
                        <button 
                            className="wzc-close-preview" 
                            onClick={() => setPreviewModalVisible(false)}
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}
            
            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification(null)}
                />
            )}                {showMediaOptions && (
                    <div 
                        className="wzc-media-modal"
                        onClick={(e) => {
                            if (e.target.classList.contains('wzc-media-modal')) {
                                setShowMediaOptions(false);
                            }
                        }}
                    >
                        <div className="wzc-media-modal-content">
                            <div className="wzc-media-modal-header">
                                <h3>Choisir un type de fichier</h3>
                                <button 
                                    className="wzc-close-btn"
                                    onClick={() => setShowMediaOptions(false)}
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="wzc-media-options-grid">
                                <button 
                                    className="wzc-media-option-btn"
                                    onClick={() => {
                                        handleMediaPick('image');
                                        setShowMediaOptions(false);
                                    }}
                                >
                                    <span className="wzc-media-icon">🖼️</span>
                                    <span className="wzc-media-label">Photo</span>
                                    <span className="wzc-media-desc">JPG, PNG, GIF</span>
                                </button>
                                <button 
                                    className="wzc-media-option-btn"
                                    onClick={() => {
                                        handleMediaPick('video');
                                        setShowMediaOptions(false);
                                    }}
                                >
                                    <span className="wzc-media-icon">🎥</span>
                                    <span className="wzc-media-label">Vidéo</span>
                                    <span className="wzc-media-desc">MP4, AVI, MOV</span>
                                </button>
                                <button 
                                    className="wzc-media-option-btn"
                                    onClick={() => {
                                        handleAttachmentSelect();
                                        setShowMediaOptions(false);
                                    }}
                                >
                                    <span className="wzc-media-icon">📄</span>
                                    <span className="wzc-media-label">Document</span>
                                    <span className="wzc-media-desc">PDF, DOC, XLS, etc.</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            {/* Message Popup Menu - Rendered in document body via portal */}
            {showMessageMenu && selectedMessage && ReactDOM.createPortal(
                <div 
                    className="wzc-popup-menu"
                    style={{
                        position: 'fixed',
                        left: menuPosition.x,
                        top: menuPosition.y,
                        zIndex: 999
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="wzc-popup-content">
                        {/* Quick emoji reactions */}
                        <div className="wzc-quick-reactions">
                            <div className="wzc-quick-reactions-title">Réactions rapides</div>
                            <div className="wzc-quick-reactions-row">
                                {['❤️', '👍', '😂', '😮', '😢', '😍'].map((emoji) => (
                                    <button
                                        key={emoji}
                                        className="wzc-quick-reaction-btn"
                                        onClick={() => handleQuickReaction(selectedMessage, emoji)}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="wzc-popup-actions">
                            {selectedMessage?.sender_id?.toString() === userId?.toString() && (
                                <>
                                    <button 
                                        className="wzc-popup-action-btn wzc-popup-edit"
                                        onClick={() => handleEditMessage(selectedMessage)}
                                    >
                                        <span className="wzc-popup-action-icon">✏️</span>
                                        <span className="wzc-popup-action-label">Modifier</span>
                                    </button>

                                    <button 
                                        className="wzc-popup-action-btn wzc-popup-delete"
                                        onClick={() => handleDeleteMessage(selectedMessage)}
                                    >
                                        <span className="wzc-popup-action-icon">🗑️</span>
                                        <span className="wzc-popup-action-label">Supprimer</span>
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Message preview */}
                        <div className="wzc-popup-preview">
                            <div className="wzc-popup-preview-content">
                                {selectedMessage.content || 'Message audio/fichier'}
                            </div>
                            <div className="wzc-popup-preview-time">
                                {moment(selectedMessage.timestamp).format('LLL')}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            
            {/* Report Modal */}
            {showReportModal && (
                <ReportModal
                    show={showReportModal}
                    onClose={() => setShowReportModal(false)}
                    onSubmit={handleReportSubmit}
                    targetType={reportTarget.type}
                    targetName={reportTarget.name}
                />
            )}
        </div>
    );
};

export default ConversationMessages;
